/*
Persistent storage helper.

iOS Safari and several Chromium variants treat IndexedDB + Cache API
quotas as evictable — when the device is low on space, the OS may
delete site data without warning. `navigator.storage.persist()` asks
the browser to mark our origin as "persistent", which raises the bar
for eviction (the OS will free non-persistent data first).

iOS Safari grants persistence ONLY when the PWA is installed via "Add
to Home Screen" (standalone mode). In a regular Safari tab the call
returns false. That's expected — we still call it, the result drives
the banner copy on the map screen.

The call is idempotent and cheap; we memoize the result for the rest
of the session so successive calls don't hit the platform API again.
*/

let fSessionResult: boolean | null = null  // memoized within the page session, cleared on reload

export interface StorageQueryAvailable
{
	supported: true
	persisted: boolean
}

export interface StorageQueryUnavailable
{
	supported: false
}

export type StorageQuery = StorageQueryAvailable | StorageQueryUnavailable

/*
Read the current persistence state without requesting it. Used to
short-circuit `tryPersistStorage` if a prior session already obtained
the grant — `navigator.storage.persisted()` is durable across reloads.
*/
export async function queryStoragePersistence(): Promise<StorageQuery>
{
	if (typeof navigator === 'undefined' || !('storage' in navigator)) return { supported: false }
	const vStorage = navigator.storage  // StorageManager, may lack persist on older browsers
	if (typeof vStorage.persisted !== 'function') return { supported: false }
	try
	{
		const vAlready = await vStorage.persisted()
		return { supported: true, persisted: vAlready }
	}
	catch
	{
		return { supported: false }
	}
}

export interface StorageEstimate
{
	quotaBytes: number  // total bytes the origin is allowed to consume — 0 if unknown
	usageBytes: number  // bytes currently consumed across IDB + Cache API + OPFS
	usageFraction: number  // usageBytes / quotaBytes, clamped to [0, 1]
}

/*
Read the browser's storage quota + current usage. Used by the profile
screen to surface a "backup recommended" banner when the origin is
close to its limit (iOS may evict at any threshold but typically
warns the user near 80%). Returns null when the API is unavailable
or returns a zero / missing quota.
*/
export async function estimateStorage(): Promise<StorageEstimate | null>
{
	if (typeof navigator === 'undefined' || !('storage' in navigator)) return null
	const vStorage = navigator.storage
	if (typeof vStorage.estimate !== 'function') return null
	try
	{
		const vRaw = await vStorage.estimate()  // { quota?: number, usage?: number, … }
		const vQuota = typeof vRaw.quota === 'number' ? vRaw.quota : 0
		const vUsage = typeof vRaw.usage === 'number' ? vRaw.usage : 0
		if (vQuota <= 0) return null
		const vFraction = Math.min(1, Math.max(0, vUsage / vQuota))
		return { quotaBytes: vQuota, usageBytes: vUsage, usageFraction: vFraction }
	}
	catch
	{
		return null
	}
}

/*
Request persistent storage from the browser. Returns true if the
origin is now (or already was) persistent, false otherwise. Never
throws — unsupported browsers and rejections both resolve to false.
*/
export async function tryPersistStorage(): Promise<boolean>
{
	if (fSessionResult !== null) return fSessionResult
	const vQuery = await queryStoragePersistence()
	if (!vQuery.supported)
	{
		fSessionResult = false
		return false
	}
	if (vQuery.persisted)
	{
		fSessionResult = true
		return true
	}
	const vStorage = navigator.storage
	if (typeof vStorage.persist !== 'function')
	{
		fSessionResult = false
		return false
	}
	try
	{
		const vGranted = await vStorage.persist()
		fSessionResult = vGranted === true
		return fSessionResult
	}
	catch
	{
		fSessionResult = false
		return false
	}
}
